/**
 * Editor Store - manages editor UI state
 */

import { defineStore } from 'pinia';
import { ref, shallowRef, computed } from 'vue';
import { TileData, TileFlagKey } from '@/formats/types';

/** Available editing tools */
export enum EditorTool {
    SELECT = 'select',
    PAINT_TILE = 'paint_tile',
    PAINT_OBJECT = 'paint_object',
    ERASE_OBJECT = 'erase_object',
    EYEDROPPER = 'eyedropper',
    BUCKET_FILL = 'bucket_fill',
    TOGGLE_BLOCKED = 'toggle_blocked',
    TOGGLE_TELEPORT = 'toggle_teleport',
    TOGGLE_FARMING = 'toggle_farming',
    TOGGLE_BUILDABLE = 'toggle_buildable',
}

/** Flag edited by each flag tool */
export const FLAG_TOOLS: Partial<Record<EditorTool, TileFlagKey>> = {
    [EditorTool.TOGGLE_BLOCKED]: 'blocked',
    [EditorTool.TOGGLE_TELEPORT]: 'teleport',
    [EditorTool.TOGGLE_FARMING]: 'farming',
    [EditorTool.TOGGLE_BUILDABLE]: 'buildable',
};

/** Zoom limits */
export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 4;

/** Brush size limits */
export const MIN_BRUSH_SIZE = 1;
export const MAX_BRUSH_SIZE = 10;

/** Selection rectangle */
export interface Selection {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

/** A tile in the multi-tile brush */
export interface BrushTile {
    offsetX: number;  // relative X from brush origin
    offsetY: number;  // relative Y from brush origin
    spriteId: number;
    frame: number;
}

function getBrushBounds(brush: BrushTile[]): { width: number; height: number } {
    if (brush.length === 0) return { width: 1, height: 1 };

    let maxX = 0;
    let maxY = 0;
    for (const tile of brush) {
        maxX = Math.max(maxX, tile.offsetX);
        maxY = Math.max(maxY, tile.offsetY);
    }

    // Offsets are normalized to start at 0,0
    return { width: maxX + 1, height: maxY + 1 };
}

export const useEditorStore = defineStore('editor', () => {
    // Tool state
    const currentTool = ref<EditorTool>(EditorTool.PAINT_TILE);
    const brushSize = ref(1);

    // Selected tile/object for painting, used while the matching brush is empty
    const selectedTileSprite = ref(0);
    const selectedTileSpriteFrame = ref(0);
    const selectedObjectSprite = ref(200);
    const selectedObjectSpriteFrame = ref(0);

    // Multi-tile brush
    const tileBrush = ref<BrushTile[]>([]);
    const objectBrush = ref<BrushTile[]>([]);

    // Paint the tile brush as a pattern locked to map position (x % width,
    // y % height) instead of stamping it at the cursor. This is how stock maps
    // lay terrain, so a painted area lines up with the terrain around it.
    const alignTilePattern = ref(true);

    // View state
    const showGrid = ref(true);
    const showObjects = ref(true);
    const showBlocked = ref(false);
    const showTeleports = ref(false);
    const showFarming = ref(false);
    const showBuildable = ref(false);
    const showWater = ref(false);
    const zoom = ref(1);
    const panX = ref(0);
    const panY = ref(0);
    // Size of the map viewport in screen pixels, reported by the renderer
    const viewportWidth = ref(0);
    const viewportHeight = ref(0);
    // While on (space held), left-drag pans the view instead of editing
    const panMode = ref(false);

    // Selection
    const selection = ref<Selection | null>(null);
    const isSelecting = ref(false);

    // Copied tiles, [x][y] like the map. Shallow: it is only ever replaced.
    const clipboard = shallowRef<TileData[][] | null>(null);

    // Mouse state - tileX/tileY are -1 while the cursor is off the map
    const tileX = ref(-1);
    const tileY = ref(-1);

    // Panel visibility
    const showTilePalette = ref(true);
    const showProperties = ref(true);
    const showMinimap = ref(true);

    // Palette selection state (shared between TilePalette and SpritesheetViewer)
    const paletteTab = ref<'tiles' | 'objects'>('tiles');
    const selectedPak = ref<string | null>(null);
    const selectedPaletteSpriteId = ref<number | null>(null);

    // Computed
    const hasSelection = computed(() => selection.value !== null);

    const selectionBounds = computed(() => {
        if (!selection.value) return null;
        const { x1, y1, x2, y2 } = selection.value;
        return {
            left: Math.min(x1, x2),
            top: Math.min(y1, y2),
            right: Math.max(x1, x2),
            bottom: Math.max(y1, y2),
            width: Math.abs(x2 - x1) + 1,
            height: Math.abs(y2 - y1) + 1,
        };
    });

    // Brush bounds (for cursor preview)
    const tileBrushBounds = computed(() => getBrushBounds(tileBrush.value));
    const objectBrushBounds = computed(() => getBrushBounds(objectBrush.value));

    // Actions
    function setTool(tool: EditorTool): void {
        currentTool.value = tool;

        // Flag edits are invisible without their overlay, so turn it on
        switch (FLAG_TOOLS[tool]) {
            case 'blocked':
                showBlocked.value = true;
                break;
            case 'teleport':
                showTeleports.value = true;
                break;
            case 'farming':
                showFarming.value = true;
                break;
            case 'buildable':
                showBuildable.value = true;
                break;
        }
    }

    function setBrushSize(size: number): void {
        brushSize.value = Math.max(MIN_BRUSH_SIZE, Math.min(MAX_BRUSH_SIZE, size));
    }

    /** Make a single tile the tile brush (eyedropper) */
    function selectTile(sprite: number, frame: number): void {
        selectedTileSprite.value = sprite;
        selectedTileSpriteFrame.value = frame;
        tileBrush.value = [{ offsetX: 0, offsetY: 0, spriteId: sprite, frame }];
    }

    /** Make a single object the object brush (eyedropper, object palette) */
    function selectObject(sprite: number, frame: number): void {
        selectedObjectSprite.value = sprite;
        selectedObjectSpriteFrame.value = frame;
        objectBrush.value = [{ offsetX: 0, offsetY: 0, spriteId: sprite, frame }];
    }

    /** Replace the tile brush. Offsets are rebased so the minimum is 0,0. */
    function setTileBrush(tiles: BrushTile[]): void {
        const minX = Math.min(...tiles.map(t => t.offsetX));
        const minY = Math.min(...tiles.map(t => t.offsetY));
        tileBrush.value = tiles.map(t => ({ ...t, offsetX: t.offsetX - minX, offsetY: t.offsetY - minY }));
    }

    /** Clear the tile brush */
    function clearTileBrush(): void {
        tileBrush.value = [];
    }

    /** Clear the object brush */
    function clearObjectBrush(): void {
        objectBrush.value = [];
    }

    function setZoom(newZoom: number): void {
        zoom.value = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
    }

    function zoomIn(): void {
        setZoom(zoom.value * 1.25);
    }

    function zoomOut(): void {
        setZoom(zoom.value / 1.25);
    }

    /** Move the view so a map tile sits in the middle of the viewport */
    function centerOnTile(x: number, y: number, tileSize: number): void {
        panX.value = viewportWidth.value / 2 - (x + 0.5) * tileSize * zoom.value;
        panY.value = viewportHeight.value / 2 - (y + 0.5) * tileSize * zoom.value;
    }

    function startSelection(x: number, y: number): void {
        selection.value = { x1: x, y1: y, x2: x, y2: y };
        isSelecting.value = true;
    }

    function updateSelection(x: number, y: number): void {
        if (!selection.value) return;
        selection.value = { ...selection.value, x2: x, y2: y };
    }

    function endSelection(): void {
        isSelecting.value = false;
    }

    function clearSelection(): void {
        selection.value = null;
        isSelecting.value = false;
    }

    function setHoveredTile(x: number, y: number): void {
        tileX.value = x;
        tileY.value = y;
    }

    return {
        // Tool state
        currentTool,
        brushSize,
        selectedTileSprite,
        selectedTileSpriteFrame,
        selectedObjectSprite,
        selectedObjectSpriteFrame,
        // Multi-tile brush
        tileBrush,
        objectBrush,
        alignTilePattern,
        // View state
        showGrid,
        showObjects,
        showBlocked,
        showTeleports,
        showFarming,
        showBuildable,
        showWater,
        zoom,
        panX,
        panY,
        viewportWidth,
        viewportHeight,
        panMode,
        // Selection
        selection,
        isSelecting,
        clipboard,
        // Mouse
        tileX,
        tileY,
        // Panels
        showTilePalette,
        showProperties,
        showMinimap,
        // Palette selection
        paletteTab,
        selectedPak,
        selectedPaletteSpriteId,
        // Computed
        hasSelection,
        selectionBounds,
        tileBrushBounds,
        objectBrushBounds,
        // Actions
        setTool,
        setBrushSize,
        selectTile,
        selectObject,
        setTileBrush,
        clearTileBrush,
        clearObjectBrush,
        setZoom,
        zoomIn,
        zoomOut,
        centerOnTile,
        startSelection,
        updateSelection,
        endSelection,
        clearSelection,
        setHoveredTile,
    };
});
